import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { FiEye } from 'react-icons/fi';

// Saudi calendar day as YYYY-MM-DD — shops run on Saudi time
const saudiDay = (offsetDays = 0) => new Date(Date.now() + offsetDays * 86400000).toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' });

export default function SuperAdminSales() {
  const navigate = useNavigate();
  // ?from=&to=&shopId=&saleType= — set by the Dashboard's sales-total card
  const [params] = useSearchParams();
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState(params.get('shopId') || '');
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(params.get('from') || '');
  const [endDate, setEndDate] = useState(params.get('to') || params.get('from') || '');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [saleType, setSaleType] = useState(params.get('saleType') || '');
  const [totals, setTotals] = useState(null);

  useEffect(() => {
    superAdminApi.get('/shops').then(r => setShops(r.data.shops)).catch(() => toast.error('Failed to load shops'));
  }, []);

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superAdminApi.get('/sales', {
        params: { shopId: shopId || undefined, saleType: saleType || undefined, startDate, endDate, page, limit: 20 },
      });
      setBills(res.data.bills);
      setTotal(res.data.total);
      setPages(res.data.pages);
      setTotals(res.data.totals || null);
    } catch { toast.error('Failed to load sales'); }
    setLoading(false);
  }, [shopId, saleType, startDate, endDate, page]);

  useEffect(() => { fetchBills(); }, [fetchBills]);

  const statusBadge = (status) => {
    const map = { paid: 'badge-success', pending: 'badge-warning', partial: 'badge-info' };
    return <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>;
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <label className="form-label" style={{ marginBottom: 0 }}>Shop</label>
          <select className="form-control" style={{ width: 220 }} value={shopId} onChange={e => { setShopId(e.target.value); setPage(1); }}>
            <option value="">All Shops</option>
            {shops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          <select className="form-control" style={{ width: 180 }} value={saleType} onChange={e => { setSaleType(e.target.value); setPage(1); }}>
            <option value="">All sales</option>
            <option value="order">Stitching orders</option>
            <option value="quick">Quick sale (counter items)</option>
          </select>
          <input type="date" className="form-control" style={{ width: 160 }}
            value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} />
          <span style={{ color: '#94a3b8' }}>to</span>
          <input type="date" className="form-control" style={{ width: 160 }}
            value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} />
          {[
            ['Today', saudiDay(0), saudiDay(0)],
            ['Yesterday', saudiDay(-1), saudiDay(-1)],
            ['Last 7 days', saudiDay(-6), saudiDay(0)],
            ['This month', `${saudiDay(0).slice(0, 8)}01`, saudiDay(0)],
          ].map(([label, f, t]) => (
            <button key={label} className={`btn btn-sm ${startDate === f && endDate === t ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => { setStartDate(f); setEndDate(t); setPage(1); }}>{label}</button>
          ))}
          {(startDate || endDate) && (
            <button className="btn btn-sm btn-outline" onClick={() => { setStartDate(''); setEndDate(''); setPage(1); }}>All dates</button>
          )}
          <span style={{ color: '#94a3b8', fontSize: 12 }}>Dates are Saudi days</span>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span style={{ color: '#64748b', fontSize: 13 }}>
            {total} bills{totals && <> · Total <strong>SAR {Number(totals.amount || 0).toFixed(0)}</strong>{totals.remaining > 0 && <> · Pending SAR {Number(totals.remaining).toFixed(0)}</>}</>}
          </span>
        </div>

        {loading ? <div className="loading"><div className="spinner"></div></div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Bill #</th>
                  <th>Shop</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>Remaining</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bills.length === 0 && (
                  <tr><td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-state-icon">🧾</div>
                      <h3>No sales found</h3>
                    </div>
                  </td></tr>
                )}
                {bills.map(bill => (
                  <tr key={bill._id}>
                    <td>
                      <span style={{ fontWeight: 700, color: '#3b82f6', cursor: 'pointer' }}
                        onClick={() => navigate(`/superadmin/sales/${bill._id}`)}>
                        #{bill.billNumber}
                      </span>
                    </td>
                    <td>{bill.shopId?.name || '—'}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{bill.customerName} {bill.isQuickSale && <span className="badge badge-info">Quick sale</span>}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{bill.customerPhone}</div>
                    </td>
                    <td>{new Date(bill.billDate).toLocaleDateString('en-AE', { timeZone: 'Asia/Riyadh', day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td><strong>SAR {bill.total?.toFixed(0)}</strong></td>
                    <td style={{ color: bill.remaining > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
                      SAR {bill.remaining?.toFixed(0)}
                    </td>
                    <td>{statusBadge(bill.status)}</td>
                    <td>
                      <button className="btn btn-outline btn-sm btn-icon" title="View"
                        onClick={() => navigate(`/superadmin/sales/${bill._id}`)}>
                        <FiEye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div style={{ padding: '12px 16px' }}>
            <div className="pagination">
              {Array.from({ length: pages }, (_, i) => (
                <button key={i} className={`page-btn ${page === i + 1 ? 'active' : ''}`}
                  onClick={() => setPage(i + 1)}>{i + 1}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
